import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material'
import {
  PersonAdd as PersonAddIcon,
  RemoveCircleOutline as RemoveIcon,
  TrendingUp as TrendingUpIcon,
  ReportProblem as RiskIcon,
  SentimentDissatisfied as EngagementIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import {
  interventionConfigsAPI,
  patientsAPI,
  userAPI,
  assessmentResultsAPI,
  assessmentsAPI,
} from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const enrollmentStatusStyles = {
  active: { color: '#16a34a', label: 'Active' },
  paused: { color: '#ca8a04', label: 'Paused' },
  withdrawn: { color: '#dc2626', label: 'Withdrawn' },
  none: { color: '#71717a', label: 'None' },
}

function StatusChip({ status }) {
  const style = enrollmentStatusStyles[status] || enrollmentStatusStyles.none
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: style.color }} />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      {Icon && <Icon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />}
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1 }} />
      {action}
    </Box>
  )
}

function PlaceholderTile({ icon: Icon, title, description }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <SectionHeader icon={Icon} title={title} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
          Coming Soon
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 300 }}>
          {description}
        </Typography>
      </Box>
    </Paper>
  )
}

function AssessmentResponseMatrix({ interventionId, participants }) {
  const [instruments, setInstruments] = useState([])
  const [selectedInstrument, setSelectedInstrument] = useState('')
  const [responsesByUser, setResponsesByUser] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Load assessment instruments
  useEffect(() => {
    let cancelled = false
    assessmentsAPI.getAssessments()
      .then(res => {
        if (!cancelled) {
          const list = res.data || []
          setInstruments(list)
          if (list.length > 0 && !selectedInstrument) {
            setSelectedInstrument(list[0].code)
          }
        }
      })
      .catch(err => {
        console.error('Failed to load assessment instruments:', err)
      })
    return () => { cancelled = true }
  }, [])

  // Fetch all participants' latest result for the chosen instrument
  useEffect(() => {
    if (!selectedInstrument || participants.length === 0) {
      setResponsesByUser({})
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)

    // Enrollment rows expose their own integer `id` (the enrollment row) and
    // `user_id` (the patient's UUID). Always use user_id for patient-scoped
    // API calls; fall back to p.id only if the row is shaped differently.
    const getUserId = (p) => p.user_id || p.id
    Promise.all(
      participants.map(p =>
        assessmentResultsAPI.getAssessmentResultsByUserId(getUserId(p))
          .then(res => ({
            userId: getUserId(p),
            results: (res.data || []).filter(r => r.instrument_code === selectedInstrument),
          }))
          .catch(() => ({ userId: getUserId(p), results: [] }))
      )
    )
      .then(results => {
        if (cancelled) return
        const byUser = {}
        for (const { userId, results: userResults } of results) {
          byUser[userId] = userResults[0] || null // latest (already sorted DESC)
        }
        setResponsesByUser(byUser)
      })
      .catch(err => {
        if (!cancelled) {
          setError('Failed to load assessment responses')
          console.error(err)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [selectedInstrument, participants])

  // Collect all question IDs seen across responses
  const questionIds = useMemo(() => {
    const ids = new Set()
    for (const result of Object.values(responsesByUser)) {
      if (!result?.results) continue
      const responses = result.results.responses || {}
      Object.keys(responses).forEach(k => ids.add(k))
    }
    return Array.from(ids).sort()
  }, [responsesByUser])

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <SectionHeader
        icon={AssessmentIcon}
        title="Assessment Response Matrix"
        subtitle="Latest responses per participant"
        action={
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Instrument</InputLabel>
            <Select
              value={selectedInstrument}
              label="Instrument"
              onChange={(e) => setSelectedInstrument(e.target.value)}
            >
              {instruments.map(i => (
                <MenuItem key={i.code} value={i.code}>
                  {i.code}{i.name ? ` — ${i.name}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      ) : participants.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No enrolled participants to display.
        </Typography>
      ) : !selectedInstrument ? (
        <Typography variant="body2" color="text.secondary">
          Select an instrument to view the response matrix.
        </Typography>
      ) : questionIds.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No responses recorded for {selectedInstrument} among enrolled participants.
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}>
                  Participant
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Score</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Severity</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Submitted</TableCell>
                {questionIds.map(qid => (
                  <TableCell key={qid} sx={{ fontSize: '0.75rem', fontWeight: 600, textAlign: 'center', minWidth: 60 }}>
                    {qid}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {participants.map(p => {
                const userId = p.user_id || p.id
                const result = responsesByUser[userId]
                const responses = result?.results?.responses || {}
                return (
                  <TableRow key={userId} hover>
                    <TableCell sx={{ fontSize: '0.8125rem', position: 'sticky', left: 0, bgcolor: 'background.paper' }}>
                      {p.name || p.user_name || p.email || String(userId).slice(0, 8)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>
                      {result?.calculated_score ?? '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem', textTransform: 'capitalize' }}>
                      {result?.severity ? result.severity.replace('_', ' ') : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem' }}>
                      {result?.submitted_date ? new Date(result.submitted_date).toLocaleDateString() : '—'}
                    </TableCell>
                    {questionIds.map(qid => (
                      <TableCell key={qid} sx={{ fontSize: '0.8125rem', textAlign: 'center' }}>
                        {responses[qid] != null ? String(responses[qid]) : '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}

function InterventionDashboard() {
  const { hasPermission } = useAuth()
  const canUpdate = hasPermission('intervention_configs', 'update')

  const [interventions, setInterventions] = useState([])
  const [selectedInterventionId, setSelectedInterventionId] = useState('')
  const [enrollments, setEnrollments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  // Add participant dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [allUsers, setAllUsers] = useState([])
  const [userSearch, setUserSearch] = useState('')
  const [saving, setSaving] = useState(false)

  // Tab state
  const [tabIndex, setTabIndex] = useState(0)

  const fetchInterventions = useCallback(async () => {
    try {
      const data = await interventionConfigsAPI.getAll({ status: 'active' })
      setInterventions(data || [])
      setSelectedInterventionId(prev => {
        if (prev) return prev
        return data && data.length > 0 ? String(data[0].id) : ''
      })
    } catch (err) {
      console.error('Failed to load interventions:', err)
      setError('Failed to load interventions')
    }
  }, [])

  const fetchEnrollments = useCallback(async (interventionId) => {
    if (!interventionId) return
    setLoading(true)
    setError(null)
    try {
      const data = await interventionConfigsAPI.getEnrollments(interventionId, { status: 'active' })
      setEnrollments(data || [])
    } catch (err) {
      console.error('Failed to load enrollments:', err)
      setError('Failed to load enrolled patients')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchInterventions()
  }, [fetchInterventions])

  useEffect(() => {
    if (selectedInterventionId) {
      fetchEnrollments(selectedInterventionId)
    }
  }, [selectedInterventionId, fetchEnrollments])

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(null), 3000)
      return () => clearTimeout(t)
    }
  }, [successMessage])

  const openAddDialog = async () => {
    setAddDialogOpen(true)
    setUserSearch('')
    if (allUsers.length === 0) {
      try {
        const res = await userAPI.getUsers()
        setAllUsers(res.data || [])
      } catch (err) {
        console.error('Failed to load users:', err)
      }
    }
  }

  const handleAddParticipant = async (userId) => {
    if (!selectedInterventionId) return
    try {
      setSaving(true)
      await patientsAPI.assignToIntervention(userId, {
        intervention_config_id: parseInt(selectedInterventionId),
      })
      setSuccessMessage('Participant added')
      await fetchEnrollments(selectedInterventionId)
      setAddDialogOpen(false)
    } catch (err) {
      console.error('Failed to add participant:', err)
      setError(err.response?.data?.error || 'Failed to add participant')
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveParticipant = async (userId) => {
    if (!window.confirm('Withdraw this participant from the intervention?')) return
    try {
      setSaving(true)
      await patientsAPI.withdrawFromIntervention(userId, 'Removed via intervention dashboard')
      setSuccessMessage('Participant withdrawn')
      await fetchEnrollments(selectedInterventionId)
    } catch (err) {
      console.error('Failed to remove participant:', err)
      setError('Failed to remove participant')
    } finally {
      setSaving(false)
    }
  }

  const selectedIntervention = interventions.find(i => String(i.id) === String(selectedInterventionId))

  // Filter eligible users (not already enrolled)
  const enrolledIds = new Set(enrollments.map(e => e.user_id || e.id))
  const candidateUsers = allUsers.filter(u => {
    if (enrolledIds.has(u.id)) return false
    if (!userSearch) return true
    const q = userSearch.toLowerCase()
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
  })

  return (
    <Box>
      <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'flex-end', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">Intervention Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage participants and view aggregate analytics for an intervention program.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Intervention</InputLabel>
          <Select
            value={selectedInterventionId}
            label="Intervention"
            onChange={(e) => setSelectedInterventionId(e.target.value)}
          >
            {interventions.length === 0 && (
              <MenuItem value="" disabled>
                No active interventions
              </MenuItem>
            )}
            {interventions.map(i => (
              <MenuItem key={i.id} value={String(i.id)}>
                {i.name || `Intervention #${i.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      {!selectedIntervention ? (
        <Alert severity="info">Select an intervention program to view its dashboard.</Alert>
      ) : (
        <>
          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
            >
              <Tab label="Participants" sx={{ minHeight: 40, textTransform: 'none' }} />
              <Tab label="Assessment Responses" sx={{ minHeight: 40, textTransform: 'none' }} />
              <Tab label="Trends & Signals" sx={{ minHeight: 40, textTransform: 'none' }} />
            </Tabs>
          </Paper>

          {/* Participants Tab */}
          {tabIndex === 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SectionHeader
                icon={PeopleIcon}
                title="Enrolled Participants"
                subtitle={`${enrollments.length} active · ${selectedIntervention.name || ''}`}
                action={
                  canUpdate && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PersonAddIcon />}
                      onClick={openAddDialog}
                      sx={{ textTransform: 'none' }}
                    >
                      Add Participant
                    </Button>
                  )
                }
              />
              {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={28} />
                </Box>
              ) : enrollments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No participants enrolled yet.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Participant</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Enrolled</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, width: 60 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {enrollments.map(e => {
                        const userId = e.user_id || e.id
                        return (
                          <TableRow key={userId} hover>
                            <TableCell sx={{ fontSize: '0.8125rem' }}>
                              {e.name || e.user_name || 'Unnamed'}
                            </TableCell>
                            <TableCell>
                              <StatusChip status={e.status || e.enrollment_status || 'active'} />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                              {e.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString() :
                                e.enrollment_date ? new Date(e.enrollment_date).toLocaleDateString() : '—'}
                            </TableCell>
                            <TableCell>
                              {canUpdate && (
                                <Tooltip title="Withdraw">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveParticipant(userId)}
                                    disabled={saving}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}

          {/* Assessment Responses Tab */}
          {tabIndex === 1 && (
            <AssessmentResponseMatrix
              interventionId={selectedInterventionId}
              participants={enrollments}
            />
          )}

          {/* Trends & Signals Tab */}
          {tabIndex === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <PlaceholderTile
                  icon={TrendingUpIcon}
                  title="Patient Metric Trends"
                  description="Assessment score trends, severity drift, and patient-reported outcome trajectories across the intervention cohort."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PlaceholderTile
                  icon={EngagementIcon}
                  title="Low Engagement"
                  description="Participants with declining session attendance, response latency, or interaction depth — flagged for clinical follow-up."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PlaceholderTile
                  icon={RiskIcon}
                  title="Trending Risks"
                  description="Emerging risk factor patterns across the cohort — new flags triggered, severity escalations, and shared risk clusters."
                />
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* Add Participant Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Participant</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or email"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
            {candidateUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No eligible users found.
              </Typography>
            ) : (
              candidateUsers.map(u => (
                <Box
                  key={u.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1,
                    px: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">{u.name || 'Unnamed'}</Typography>
                    <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleAddParticipant(u.id)}
                    disabled={saving}
                    sx={{ textTransform: 'none' }}
                  >
                    Enroll
                  </Button>
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default InterventionDashboard
